<template>
  <div
    class="selectbox"
    :class="{ disabled: readonly, error }"
    @click="onClickSelectBox"
    v-click-outside="onClickOutside"
  >
    <div class="selected">
      <template>
        <div v-if="text" class="text">{{ text }}</div>
        <SelectBoxItem v-else-if="selected" :name="selected.name" :image="selected.image || placeholderImage" />
        <div v-else class="placeholder">{{ allowUnselected ? $t('common.unselected') : placeholder }}</div>
      </template>
      <ChevronDownIcon v-if="!readonly" class="caret" :class="{ open: isOpen }" :fillColor="accentColor" />
    </div>

    <div v-if="isOpen" class="dropdown">
      <SelectBoxItem
        v-if="allowUnselected"
        class="item"
        :name="$t('common.unselected')"
        :image="placeholderImage"
        :key="undefined"
        @click.native="onClickItem(undefined)"
      />
      <template v-for="option in options">
        <SelectBoxItem
          class="item"
          :name="option.name"
          :image="option.image || placeholderImage"
          :key="option.id"
          @click.native="onClickItem(option)"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import SelectBoxItem, { SelectBoxItemProps } from "@/components/atoms/SelectBoxItem.vue";
import ChevronDownIcon from "icons/ChevronDown.vue";

const ClickOutside = require("vue-click-outside");

export default Vue.extend<Data, Methods, Computed, Props>({
  directives: { ClickOutside },
  components: { SelectBoxItem, ChevronDownIcon },
  model: {
    prop: "selected",
    event: "select",
  },
  props: {
    selected: Object as PropType<SelectBoxItemProps>,
    initialSelected: Object as PropType<object>,
    options: Array as PropType<Option[]>,
    placeholder: String as PropType<string>,
    placeholderImage: String as PropType<string>,
    onClickHandler: Function as PropType<() => void>,
    text: String as PropType<string>,
    allowUnselected: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    readonly: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    error: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      themeWatcher: null as MutationObserver | null, // テーマ変更検知用
    };
  },
  computed: {
    accentColor() {
      // CSS変数からアクセント色を取得
      if (typeof window !== "undefined") {
        const computedStyle = getComputedStyle(document.documentElement);
        return computedStyle.getPropertyValue("--color-primary").trim() || "#76a4d3";
      }
      return "#76a4d3";
    },
  },
  created() {
    if (this.initialSelected) {
      this.$emit("select", this.initialSelected);
    }
    // テーマ変更を検知するMutationObserverを設定
    if (typeof window !== "undefined") {
      this.themeWatcher = new MutationObserver(() => {
        // テーマ変更時にcomputed propertyを再計算させるため、$forceUpdateを呼び出す
        this.$forceUpdate();
      });
      this.themeWatcher.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-app-theme"],
      });
    }
  },
  beforeDestroy() {
    // MutationObserverをクリーンアップ
    if (this.themeWatcher) {
      this.themeWatcher.disconnect();
      this.themeWatcher = null;
    }
  },
  methods: {
    onClickSelectBox() {
      if (this.readonly) return;

      if (this.onClickHandler) {
        this.onClickHandler();
      } else {
        this.isOpen = !this.isOpen;
      }
    },
    onClickItem(item) {
      this.$emit("select", item);
    },
    onClickOutside() {
      this.isOpen = false;
    },
  },
});

interface Props {
  selected: SelectBoxItemProps;
  initialSelected?: object;
  options: Option[];
  placeholder: string;
  placeholderImage?: string;
  onClickHandler?: () => void;
  text?: string;
  allowUnselected: boolean;
  readonly: boolean;
  error: boolean;
}

interface Data {
  isOpen: boolean;
  themeWatcher: MutationObserver | null;
}

interface Computed {
  accentColor: string;
}

interface Methods {
  onClickSelectBox(): void;
  onClickItem(item?: Option): void;
  onClickOutside(): void;
}

export type Option = {
  id: string | number;
  name: string;
  image?: string;
};
</script>

<style lang="scss" scoped>
.selectbox {
  position: relative;
  box-sizing: border-box;
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-xs);
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);

  &.disabled {
    border-color: var(--color-border);
    pointer-events: none;
  }

  &.error {
    border-color: var(--color-warning);
  }

  .selected {
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    padding: 10px 20px;

    .text {
      font-weight: var(--font-weight-bold);
    }

    .placeholder {
      color: var(--color-text-low);
    }

    .caret {
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.3s;

      &.open {
        transform: rotate(180deg);
      }
    }
  }

  .dropdown {
    position: absolute;
    background-color: var(--color-background-normal);
    width: 100%;
    box-shadow: var(--shadow-md);
    border-radius: 0 0 5px 5px;
    z-index: 1;
    border: 1px solid var(--color-border);
    border-top: none;

    .item {
      box-sizing: border-box;
      padding: 10px 20px;
      color: var(--color-text-normal);

      &:hover {
        color: var(--color-text-on-fixed-bg);
        background-color: var(--color-primary);
      }
    }
  }
}
</style>
